import useUsers from 'js/social/Feed/hooks/useUsers';
import useNews from 'js/social/Feed/hooks/useNews';
import { NewsType, Comment, FollowableUser } from 'js/social/shared/types';

// This file provides utilities for combining multiple sources of data for the
// newsfeed and ensuring that updates to one affect both. For example, this
// might be useful if we render the same news in multiple places and we want
// updates to one to propogate to both.

const clone =
  typeof window !== 'undefined'
    ? window.structuredClone
    : <T>(obj: T): T => obj;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function combineAction<T extends (...args: any[]) => void>(
  outerAction: T | undefined,
  innerAction: T,
): (...args: Parameters<T>) => void {
  return (...args) => {
    outerAction?.(...args);
    innerAction(...args);
  };
}

// This combines the stateful user actions from two separate results of
// useUsers() and produces actions that call both of them.
export function mergeUserActions(
  outerActions: ReturnType<typeof useUsers> | undefined,
  innerActions: ReturnType<typeof useUsers>,
): ReturnType<typeof useUsers> {
  const mergedActions = {
    ...innerActions,
    setMoreUsers: (users: FollowableUser[]) => {
      // This can't be handled by combineAction because we need to clone the
      // users to prevent cross-pollination
      outerActions?.setMoreUsers(clone(users));
      innerActions.setMoreUsers(users);
    },
    setUserFollowing: combineAction(
      outerActions?.setUserFollowing,
      innerActions.setUserFollowing,
    ),
  };

  return mergedActions;
}

// This combines the stateful news actions from two separate results of
// useNews() and produces actions that call both of them.
// It doesn't merge all of the actions because some of them are weird and we
// don't want all places calling them.
export function mergeNewsActions(
  outerActions: ReturnType<typeof useNews> | undefined,
  innerActions: ReturnType<typeof useNews>,
) {
  const mergedActions = {
    ...innerActions,
    removeUserContent: combineAction(
      outerActions?.removeUserContent,
      innerActions.removeUserContent,
    ),
    updateFeedNews: combineAction(
      outerActions?.updateFeedNews,
      innerActions.updateFeedNews,
    ),
    likeFeedNews: combineAction(
      outerActions?.likeFeedNews,
      innerActions.likeFeedNews,
    ),
    unlikeFeedNews: combineAction(
      outerActions?.unlikeFeedNews,
      innerActions.unlikeFeedNews,
    ),
    repostFeedNews: combineAction(
      outerActions?.repostFeedNews,
      innerActions.repostFeedNews,
    ),
    unrepostFeedNews: combineAction(
      outerActions?.unrepostFeedNews,
      innerActions.unrepostFeedNews,
    ),
    updateWorkBookmarkStatus: combineAction(
      outerActions?.updateWorkBookmarkStatus,
      innerActions.updateWorkBookmarkStatus,
    ),
    addFeedComment: (
      type: NewsType,
      commentableId: number | string,
      parentCommentId: number | string | null,
      comment: Comment,
      users: FollowableUser[],
    ) => {
      // This can't be handled by combineAction because we need to clone the
      // comment and users to prevent cross-pollination
      outerActions?.addFeedComment(
        type,
        commentableId,
        parentCommentId,
        clone(comment),
        clone(users),
      );
      innerActions.addFeedComment(
        type,
        commentableId,
        parentCommentId,
        comment,
        users,
      );
    },
    removeFeedComment: combineAction(
      outerActions?.removeFeedComment,
      innerActions.removeFeedComment,
    ),
    updateFeedComment: combineAction(
      outerActions?.updateFeedComment,
      innerActions.updateFeedComment,
    ),
    likeFeedComment: combineAction(
      outerActions?.likeFeedComment,
      innerActions.likeFeedComment,
    ),
    unlikeFeedComment: combineAction(
      outerActions?.unlikeFeedComment,
      innerActions.unlikeFeedComment,
    ),
  };

  return mergedActions;
}
